import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search, 
  Plus,
  MessageSquare,
  Clock,
  User,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Pagination from '../components/Pagination';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const styles = {
    draft: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-400',
    pending: 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400',
    approved: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400',
    rejected: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400',
    in_review: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400',
    on_hold: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400',
    needs_info: 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400'
  };

  return (
    <span className={`px-2 py-1 text-xs rounded ${styles[status] || styles.draft}`}>
      {status?.replace('_', ' ')}
    </span>
  );
};

export default function CostCardsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const canViewAll = hasRole('admin') || 
    user?.permissions?.includes('cogs_cards.view_all') ||
    user?.permissions?.includes('admin.full_access');
  
  // Default to 'all' for admin users, 'mine' for others
  const [ownerFilter, setOwnerFilter] = useState('all');
  
  // Update owner filter when canViewAll changes (e.g., on initial load)
  useEffect(() => {
    if (!canViewAll) {
      setOwnerFilter('mine');
    }
  }, [canViewAll]);

  useEffect(() => {
    fetchCards();
  }, [statusFilter]);

  const fetchCards = async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/cost-cards${params}`);
      setCards(response.data);
    } catch (error) {
      toast.error('Failed to load cost cards');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    const newParams = {};
    if (status !== 'all') newParams.status = status;
    setSearchParams(newParams);
  };

  const handleOwnerChange = (owner) => {
    setOwnerFilter(owner);
  };

  const handleCardClick = (card) => {
    navigate(`/cost-cards/${card.id}`);
  };

  const filteredCards = cards.filter(card => {
    const matchesSearch = searchQuery === '' || 
      card.part_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      card.part_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesOwner = true;
    if (canViewAll && ownerFilter !== 'all') {
      if (ownerFilter === 'created') {
        matchesOwner = card.created_by === user?.id;
      } else if (ownerFilter === 'assigned') {
        matchesOwner = card.assigned_to === user?.id;
      } else if (ownerFilter === 'mine') {
        matchesOwner = card.created_by === user?.id || card.assigned_to === user?.id;
      }
    }
    // For 'all' filter or non-admin users, show all cards they have access to
    
    return matchesSearch && matchesOwner;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredCards.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCards = filteredCards.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, ownerFilter]);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin w-8 h-8 border-2 border-[#C3002F] border-t-transparent rounded-full" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-8 space-y-6" data-testid="cost-cards-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-zinc-900 dark:text-white tracking-tight">COGS Cards</h1>
            <p className="text-zinc-500 dark:text-zinc-400 mt-1">Manage and approve pricing cost cards</p>
          </div>
          <Button
            onClick={() => navigate('/parts')}
            className="bg-[#C3002F] hover:bg-[#9f0026]"
            data-testid="create-card-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create COGS Card
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by part number or name..."
              className="pl-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-800"
              data-testid="search-cards-input"
            />
          </div>
          
          {canViewAll && (
            <Select value={ownerFilter} onValueChange={handleOwnerChange}>
              <SelectTrigger className="w-44 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800">
                <SelectItem value="all">
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4" /> All Cards
                  </span>
                </SelectItem>
                <SelectItem value="mine">
                  <span className="flex items-center gap-2">
                    <User className="w-4 h-4" /> My Cards
                  </span>
                </SelectItem>
                <SelectItem value="created">
                  <span className="flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Created by Me
                  </span>
                </SelectItem>
                <SelectItem value="assigned">
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Assigned to Me
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          )}

          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-40 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-800" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_review">In Review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="on_hold">On Hold</SelectItem>
              <SelectItem value="needs_info">Needs Info</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedCards.length > 0 ? (
            paginatedCards.map((card) => (
              <div
                key={card.id}
                onClick={() => handleCardClick(card)}
                className={`p-5 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg cursor-pointer transition-all duration-200 hover:border-zinc-300 dark:hover:border-zinc-700 hover:shadow-md dark:hover:bg-zinc-900 ${
                  card.status === 'rejected' ? 'border-l-2 border-l-red-500' :
                  card.status === 'pending' ? 'border-l-2 border-l-amber-500' :
                  card.status === 'in_review' ? 'border-l-2 border-l-blue-500' :
                  card.status === 'approved' ? 'border-l-2 border-l-emerald-500' :
                  card.status === 'on_hold' ? 'border-l-2 border-l-orange-500' :
                  card.status === 'needs_info' ? 'border-l-2 border-l-purple-500' : ''
                }`}
                data-testid={`card-${card.id}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-mono text-zinc-900 dark:text-white font-semibold">{card.part_number}</p>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">{card.part_name}</p>
                  </div>
                  <StatusBadge status={card.status} />
                </div>

                {/* Family */}
                <p className="text-xs text-zinc-500 mb-3">{card.family_name}</p>

                {/* Pricing */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-2 bg-zinc-50 dark:bg-zinc-800/50 rounded">
                    <p className="text-xs text-zinc-500">DBP</p>
                    <p className="font-mono text-emerald-600 dark:text-emerald-400 font-semibold">
                      ₹{(card.dbp || card.dealer_price || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-2 bg-zinc-50 dark:bg-zinc-800/50 rounded">
                    <p className="text-xs text-zinc-500">MRP</p>
                    <p className="font-mono text-amber-600 dark:text-amber-400 font-semibold">
                      ₹{(card.mrp || 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-zinc-200 dark:border-zinc-800">
                  <div className="flex items-center gap-1 text-xs text-zinc-500">
                    <Clock className="w-3 h-3" />
                    {new Date(card.updated_at).toLocaleDateString()}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-500">{card.created_by_name}</span>
                  </div>
                </div>

                {/* Rejection indicator */}
                {card.status === 'rejected' && card.approver_comments && (
                  <div className="mt-3 p-2 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded text-xs text-red-600 dark:text-red-400 flex items-start gap-2">
                    <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-2">{card.approver_comments}</span>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-16 text-zinc-500">
              No cost cards found
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredCards.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredCards.length}
            itemsPerPage={itemsPerPage}
            className="mt-6"
          />
        )}
      </div>
    </MainLayout>
  );
}
